import React, { useMemo } from "react";
import {
  Building2,
  Hash,
  Phone,
  BadgeCheck,
  BadgeX,
  CalendarClock,
  CreditCard,
  Image as ImageIcon,
  Settings2,
} from "lucide-react";

const dash = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const getCode = (row) =>
  row.us_code || row.user_code || `${row.fk_user_id || ""}`;

const getStatus = (row) => row.us_status || row.status || row.active || "1";

export default function ClientOrgTypeTable({
  rows = [],
  loading = false,
  search = "",
  onConfigure,
  currentPage = 1,
  pageSize = 100,
}) {
  const filteredRows = useMemo(() => {
    const searchValue = String(search || "")
      .toLowerCase()
      .trim();

    return rows.filter((row) => {
      const code = getCode(row);

      return (
        !searchValue ||
        String(row.organization_name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.whatsapp_number || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.payment_gateway || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(code || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.fk_user_id || "")
          .toLowerCase()
          .includes(searchValue)
      );
    });
  }, [rows, search]);

  const normalizedCurrentPage = Math.max(Number(currentPage) || 1, 1);
  const normalizedPageSize = Math.max(Number(pageSize) || 100, 1);

  /*
    API kabhi poore records return kar rahi hai aur kabhi current page ke records.
    Isliye:
    - rows > pageSize: client-side slice
    - rows <= pageSize: API already paginated, direct show
  */
  const visibleRows = useMemo(() => {
    if (filteredRows.length > normalizedPageSize) {
      const startIndex = (normalizedCurrentPage - 1) * normalizedPageSize;
      const endIndex = startIndex + normalizedPageSize;

      return filteredRows.slice(startIndex, endIndex);
    }

    return filteredRows;
  }, [filteredRows, normalizedCurrentPage, normalizedPageSize]);

  if (loading) {
    return (
      <div className="grid place-items-center rounded-2xl border bg-white py-10 text-sm text-gray-500">
        Loading clients…
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="overflow-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#01C37D] text-white">
            <tr>
              <th className="w-16 px-4 py-3 text-center font-semibold">
                S.No.
              </th>
              <th className="px-10 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Code / ID</th>
              <th className="px-4 py-3 font-semibold">GST / PAN</th>
              <th className="px-4 py-3 font-semibold">Gateway</th>
              <th className="px-4 py-3 font-semibold">Login / Status</th>
              <th className="px-10 py-3 text-right font-semibold">Action</th>
            </tr>
          </thead>

          <tbody>
            {visibleRows.map((row, index) => {
              const code = getCode(row);
              const status = getStatus(row);

              const isActive =
                String(status) === "1" ||
                String(status).toLowerCase() === "y" ||
                String(status).toLowerCase() === "active";

              const serialNumber =
                (normalizedCurrentPage - 1) * normalizedPageSize + index + 1;

              return (
                <tr
                  key={`${
                    row.pk_info_id ||
                    row.id ||
                    row.fk_user_id ||
                    code ||
                    "client"
                  }-${serialNumber}`}
                  className="border-t transition hover:bg-emerald-50/40"
                >
                  <td className="px-4 py-3 text-center align-middle">
                    <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-gray-100 px-2 text-xs font-semibold text-gray-700">
                      {serialNumber}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl border bg-gray-50">
                        {row.imageUrl ? (
                          <img
                            src={row.imageUrl}
                            alt={row.organization_name || "Client"}
                            className="h-full w-full object-cover"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <Building2 size={18} className="text-gray-500" />
                        )}
                      </div>

                      <div>
                        <div className="font-semibold text-gray-900">
                          {dash(row.organization_name)}
                        </div>

                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                          <Phone size={12} />
                          {dash(row.whatsapp_number)}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1 rounded-xl bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                      <Hash size={13} />
                      {dash(code)}
                    </div>

                    <div className="mt-1 text-xs text-gray-500">
                      User ID: {dash(row.fk_user_id)}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold text-gray-700">
                      GST: {dash(row.gst)}
                    </div>

                    <div className="mt-1 text-xs text-gray-500">
                      PAN: {dash(row.pan_no)}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1 rounded-xl border border-violet-100 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                      <CreditCard size={13} />
                      {dash(row.payment_gateway)}
                    </div>

                    <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                      <ImageIcon size={12} />
                      QR: {row.qr_code ? "Available" : "—"}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        isActive
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-rose-200 bg-rose-50 text-rose-700"
                      }`}
                    >
                      {isActive ? (
                        <BadgeCheck size={13} />
                      ) : (
                        <BadgeX size={13} />
                      )}

                      {isActive ? "Active" : "Inactive"}
                    </div>

                    <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                      <CalendarClock size={12} />
                      {dash(row.us_last_login || row.opening_time)}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right align-middle">
                    <button
                      type="button"
                      onClick={() => onConfigure?.(row)}
                      className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-medium hover:bg-gray-50"
                    >
                      <Settings2 size={14} />
                      Configure
                    </button>
                  </td>
                </tr>
              );
            })}

            {!visibleRows.length && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
